import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/challengesService";
import type { ChallengesSection, ChallengePair } from "../types";

interface ChallengesState {
  section: ChallengesSection | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: ChallengesState = { section: null, loading: false, saving: false, error: null };

export const fetchChallenges = createAsyncThunk("cms/challenges/fetch", api.getChallenges);
export const saveChallengesSection = createAsyncThunk(
  "cms/challenges/save",
  (payload: Pick<ChallengesSection, "headingLine1" | "headingLine2" | "subtitle">) => api.updateChallengesSection(payload)
);
export const addChallengePair = createAsyncThunk("cms/challenges/addPair", api.addPair);
export const editChallengePair = createAsyncThunk(
  "cms/challenges/editPair",
  ({ id, payload }: { id: number; payload: Omit<ChallengePair, "id"> }) => api.updatePair(id, payload)
);
export const removeChallengePair = createAsyncThunk("cms/challenges/removePair", api.removePair);

const challengesSlice = createSlice({
  name: "cmsChallenges",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchChallenges.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchChallenges.fulfilled, (state, action: PayloadAction<ChallengesSection>) => {
        state.loading = false;
        state.section = action.payload;
      })
      .addCase(fetchChallenges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load challenges section.";
      })
      .addCase(saveChallengesSection.pending, (state) => { state.saving = true; })
      .addCase(saveChallengesSection.fulfilled, (state, action: PayloadAction<ChallengesSection>) => {
        state.saving = false;
        state.section = action.payload;
      })
      .addCase(saveChallengesSection.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to save section.";
      })
      .addCase(addChallengePair.fulfilled, (state, action: PayloadAction<ChallengePair>) => {
        state.section?.pairs.push(action.payload);
      })
      .addCase(editChallengePair.fulfilled, (state, action: PayloadAction<ChallengePair>) => {
        const idx = state.section?.pairs.findIndex((p) => p.id === action.payload.id) ?? -1;
        if (state.section && idx !== -1) state.section.pairs[idx] = action.payload;
      })
      .addCase(removeChallengePair.fulfilled, (state, action: PayloadAction<number>) => {
        if (state.section) {
          state.section.pairs = state.section.pairs.filter((p) => p.id !== action.payload);
        }
      });
  },
});

export default challengesSlice.reducer;